import React from "react";

/**
 * Shared OrderTimeline component — extracted from OrderTracker.jsx as part
 * of Iteration K so both the public tracker page and the logged-in
 * customer dashboard order-detail page render the SAME timeline.
 *
 * Ownership is enforced by whoever renders this component; the timeline
 * itself is presentational.
 */

const TIMELINE_STEPS_PICKUP = [
  { code: "awaiting_payment",   label: "Order Submitted" },
  { code: "payment_submitted",  label: "Payment Submitted" },
  { code: "payment_verified",   label: "Payment Confirmed" },
  { code: "preparing",          label: "Preparing" },
  { code: "ready",              label: "Ready for Pickup" },
  { code: "completed",          label: "Completed" },
];

const TIMELINE_STEPS_SHIP = [
  { code: "awaiting_payment",   label: "Order Submitted" },
  { code: "payment_submitted",  label: "Payment Submitted" },
  { code: "payment_verified",   label: "Payment Confirmed" },
  { code: "preparing",          label: "Preparing" },
  { code: "shipped",            label: "Shipped" },
  { code: "completed",          label: "Completed" },
];

const STATUS_ORDER = {
  awaiting_payment: 0,
  payment_submitted: 1,
  payment_verified: 2,
  preparing: 3,
  ready: 4,
  shipped: 4,
  completed: 5,
};

export default function OrderTimeline({ order }) {
  const isShip = ((order.delivery || {}).code || "").startsWith("ups");
  const steps = isShip ? TIMELINE_STEPS_SHIP : TIMELINE_STEPS_PICKUP;
  const currentRank = STATUS_ORDER[order.status] ?? 0;
  return (
    <div
      className="rounded-2xl border-4 border-[var(--ink)] bg-white p-4 md:p-5"
      data-testid="order-timeline"
    >
      <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)] mb-3">
        Order status
      </div>
      <ol className="grid gap-3 md:grid-cols-6 md:gap-2">
        {steps.map((s, i) => {
          const rank = STATUS_ORDER[s.code] ?? i;
          const done = rank < currentRank;
          const active = rank === currentRank && order.status !== "cancelled";
          const dotCls = active
            ? "bg-[var(--panini-red)] text-white ring-4 ring-[var(--panini-yellow)]/60"
            : done
              ? "bg-emerald-600 text-white"
              : "bg-[var(--stone)] text-[var(--smoke)]";
          return (
            <li
              key={s.code}
              className="flex md:flex-col md:items-center items-center gap-3 md:gap-2"
              data-testid={`timeline-step-${s.code}`}
            >
              <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs ${dotCls}`}>
                {done ? "✓" : i + 1}
              </div>
              <div className="text-xs md:text-center leading-tight">
                <div
                  className={`font-bold ${
                    active
                      ? "text-[var(--panini-red)]"
                      : done
                        ? "text-emerald-700"
                        : "text-[var(--smoke)]"
                  }`}
                >
                  {s.label}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
      {order.status === "cancelled" && (
        <div
          className="mt-3 text-sm font-bold text-[var(--panini-red)]"
          data-testid="timeline-cancelled"
        >
          This order was cancelled.
        </div>
      )}
    </div>
  );
}
